import AsyncStorage from '@react-native-async-storage/async-storage';
import { withTimeout } from './withTimeout';

// Trial preferences belong to this browser/device, not a temporary Firebase UID.
// Separate keys avoid losing another popup's opt-out during concurrent writes.
export const TRIAL_ADVERTISEMENT_KEY_PREFIX = 'hexia:trial:advertisement-opt-out:v1:';
const STORAGE_TIMEOUT_MS = 5000;

interface TrialPreferences {
  loaded: boolean;
  codes: readonly string[];
}

let snapshot: TrialPreferences = { loaded: false, codes: [] };
let pendingLoad: Promise<void> | undefined;
const listeners = new Set<() => void>();

const publish = (codes: readonly string[], loaded: boolean) => {
  const merged = [...new Set([...snapshot.codes, ...codes])];
  if (loaded === snapshot.loaded && merged.length === snapshot.codes.length) return;
  snapshot = { loaded, codes: merged };
  listeners.forEach(listener => listener());
};

const readStoredCodes = async (): Promise<string[]> => {
  const keys = (await AsyncStorage.getAllKeys()).filter(key => key.startsWith(TRIAL_ADVERTISEMENT_KEY_PREFIX));
  if (!keys.length) return [];
  const entries = await AsyncStorage.multiGet(keys);
  return entries.flatMap(([key, value]) => {
    if (value !== 'true') return [];
    try {
      const code = decodeURIComponent(key.slice(TRIAL_ADVERTISEMENT_KEY_PREFIX.length));
      return code ? [code] : [];
    } catch {
      return [];
    }
  });
};

export const trialAdvertisementPreferences = {
  getSnapshot: () => snapshot,
  subscribe: (listener: () => void) => {
    listeners.add(listener);
    return () => { listeners.delete(listener); };
  },
  load: (): Promise<void> => {
    if (!pendingLoad) {
      pendingLoad = withTimeout(readStoredCodes(), STORAGE_TIMEOUT_MS)
        .then(codes => publish(codes, true))
        .catch(() => {
          // Private browsing, corrupt storage or a stalled native bridge must
          // not block onboarding. Retain this session's explicit opt-outs.
          publish([], true);
        })
        .finally(() => { pendingLoad = undefined; });
    }
    return pendingLoad;
  },
  optOut: async (code: string): Promise<void> => {
    publish([code], snapshot.loaded);
    try {
      await withTimeout(
        AsyncStorage.setItem(`${TRIAL_ADVERTISEMENT_KEY_PREFIX}${encodeURIComponent(code)}`, 'true'),
        STORAGE_TIMEOUT_MS,
      );
    } catch {
      // Dismissal still works if persistence fails; the in-memory preference
      // suppresses repeats for the remainder of this app/browser session.
    }
  },
};
